import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, Cookie, Settings } from "lucide-react";
import { applyAnalyticsConsent } from "@/lib/analyticsConsent";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    functional: false
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Phase 6A: the footer "Cookie Settings" control reopens this banner so
  // visitors always have a way to review or change their choice.
  useEffect(() => {
    const reopen = () => {
      try {
        const stored = JSON.parse(localStorage.getItem("cookie_consent") || "null");
        if (stored) setPreferences({ essential: true, functional: stored.functional === true });
      } catch { /* keep defaults */ }
      setShowSettings(true);
      setShowBanner(true);
    };
    window.addEventListener("shs:open-cookie-settings", reopen);
    return () => window.removeEventListener("shs:open-cookie-settings", reopen);
  }, []);

  // Iter 74: reserve body bottom-padding equal to the banner height while
  // the banner is visible.  Without this the banner — `fixed bottom-0 z-50`
  // — physically sits ON TOP of any submit button or CTA at the bottom of
  // the page and intercepts pointer events.  This was the actual root cause
  // of "the enquiry form isn't working" — new visitors literally couldn't
  // tap Submit because the cookie banner covered it.  The padding is
  // restored on unmount so the layout returns to normal once a choice is
  // recorded.
  useEffect(() => {
    if (!showBanner) return undefined;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const reserve = isMobile ? '220px' : '120px';
    const previous = document.body.style.paddingBottom;
    document.body.style.paddingBottom = `calc(${reserve} + env(safe-area-inset-bottom, 0px))`;
    return () => { document.body.style.paddingBottom = previous; };
  }, [showBanner]);

  const handleAcceptAll = () => {
    const consentData = {
      essential: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie_consent", JSON.stringify(consentData));
    applyAnalyticsConsent(true);
    setShowBanner(false);
  };

  const handleAcceptEssential = () => {
    const consentData = {
      essential: true,
      functional: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie_consent", JSON.stringify(consentData));
    applyAnalyticsConsent(false);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie_consent", JSON.stringify(consentData));
    applyAnalyticsConsent(preferences.functional === true);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t shadow-lg animate-in slide-in-from-bottom duration-300"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-6xl mx-auto">
        {!showSettings ? (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Cookie className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1e3a5f]">We use cookies</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  We use essential cookies to make our website work. We would also like to set optional cookies
                  to improve your experience. You can manage your preferences or accept all cookies.{" "}
                  <Link to="/cookies" className="text-[#1e3a5f] underline hover:no-underline">
                    Learn more
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="gap-2"
                data-testid="cookie-manage-btn"
              >
                <Settings className="h-4 w-4" />
                Manage
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAcceptEssential}
                data-testid="cookie-essential-btn"
              >
                Essential Only
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="bg-[#1e3a5f] hover:bg-[#2c5282]"
                data-testid="cookie-accept-all-btn"
              >
                Accept All
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#1e3a5f] flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Cookie Preferences
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-3">
              <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Essential Cookies</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Required for the website to function. Cannot be disabled.
                  </p>
                </div>
                <div className="flex items-center">
                  <span className="text-xs text-emerald-600 font-medium">Always On</span>
                </div>
              </div>

              <div className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Functional Cookies</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Enable features like remembering your preferences.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences(prev => ({ ...prev, functional: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1e3a5f]"></div>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSavePreferences}
                className="bg-[#1e3a5f] hover:bg-[#2c5282]"
              >
                Save Preferences
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Read our full{" "}
              <Link to="/cookies" className="text-[#1e3a5f] underline hover:no-underline">
                Cookie Policy
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-[#1e3a5f] underline hover:no-underline">
                Privacy Policy
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
